/**
 * Inventory Domain Module
 *
 * Handles all inventory-related operations including:
 * - Supplies (receiving goods)
 * - Transfers (between stores)
 * - Writeoffs (damaged/expired goods)
 * - Revisions (stock counts)
 *
 * This module provides proper TypeScript types for inventory operations.
 */

import { Platform } from 'react-native'
import { format } from 'date-fns'
import { getDefaultStore } from 'jotai'
import * as Sentry from '@sentry/react-native'

// Firebase primitives (direct import to avoid circular deps)
import { rootDb, fieldValue, storage } from './firebaseFirestore'

// Shared types and utilities
import {
  shouldUseSupabase,
  convertTimestamp,
  transformApiItem,
  filterUndefined,
  type DateRange,
  type TimestampLike,
  type MockRef,
} from './types'
import { getFirestoreRef, runIdempotentDelete } from './firestoreRefUtils'
import { explainRefusedCommit } from './missingProductDocuments'
import { bestEffortDualWrite, bestEffortDualWriteDelete } from './dualWrite'

import type {
  FirestoreDb,
  Organization,
} from './firestore.types'

import {
  dbAtom,
  organizationAtom,
  productsDictAtom,
} from '../AtomsProvider'

import { getLocalStoreDate, getDailyStatID, dailyStatIDForRecord } from '../../utils/timezoneFunctions'
import { suppliesApi, transfersApi, writeoffsApi, revisionsApi } from '../API/typedApiClient'
import { captureWriteRequest, createSuppliesApi } from '@atlas/api-client'
import { readWholeList, newestFirst } from '../API/readWholeList'
import type { TransferResponse } from '@atlas/api-client'
import { sanitizeForFirestoreDeep } from './firebase'
import { reportFirestoreOnlyWriterUsage } from '../analytics/firestoreOnlyWriterUsage'

import type { PaymentMethod } from './sales'
import { coerceCartItemForApi } from './sales'
import { assertWWriterAvailable } from '../Write/writeW'
import { commitGuardedCreate } from './writeClaims'

// ============================================================================
// Types
// ============================================================================

/**
 * Supply item (received product)
 */
export interface SupplyItem {
  id: string
  name?: string
  ref?: MockRef
  quantity: number
  returnQuantity?: number
  purchasePrice?: number
  price?: number
  totalPrice?: number
  product?: {
    name?: string
    purchasePrice?: number
  }
  // Product snapshot fields (stored for historical records)
  unit?: string | null
  barcode?: string | null
  category?: string | null
}

/**
 * Supply document (goods received)
 */
export interface Supply {
  id?: string
  ref?: MockRef
  organizationId: string
  store: string
  supplyDate: Date | TimestampLike
  dailyStatID: string

  // Items
  items: SupplyItem[]
  totalSum: number
  totalPrice?: number

  // Supplier
  supplierID?: string
  supplierId?: string
  supplierName?: string

  // Payment
  isPaid?: boolean
  paymentMethod?: PaymentMethod
  paymentDate?: Date | TimestampLike
  paidBy?: string
  createPaymentTransaction?: boolean
  transactionId?: string

  // Related documents
  supplyOrderId?: string
  invoiceImage?: string

  // Audit
  cashierUID?: string
  createdAt?: Date | TimestampLike
  comment?: string
}

/**
 * Transfer item (moved product)
 */
export interface TransferItem {
  id: string
  name?: string
  ref?: MockRef
  quantity: number
  purchasePrice?: number
}

/**
 * Transfer document (goods moved between stores)
 */
export interface Transfer {
  id?: string
  ref?: MockRef
  organizationId: string
  date: Date | TimestampLike
  dailyStatID: string
  dailyStatIDTo?: string

  // Items
  items: TransferItem[]
  totalSum?: number

  // Stores
  fromStore: string
  toStore: string
  fromStoreName?: string
  toStoreName?: string

  // Audit
  cashierUID?: string
  createdAt?: Date | TimestampLike
  comment?: string
}

/**
 * Writeoff item (discarded product)
 */
export interface WriteoffItem {
  id: string
  name?: string
  ref?: MockRef
  quantity: number
  purchasePrice?: number
  price?: number
}

/**
 * Writeoff reason
 */
export interface WriteoffReason {
  id: string
  text: string
}

/**
 * Writeoff document (goods discarded)
 */
export interface Writeoff {
  id?: string
  ref?: MockRef
  organizationId: string
  store: string
  date: Date | TimestampLike
  dailyStatID: string

  // Items
  items: WriteoffItem[]
  totalSum?: number
  totalPrice?: number

  // Reason
  reason?: WriteoffReason
  reasonText?: string

  // Audit
  cashierUID?: string
  createdAt?: Date | TimestampLike
  comment?: string
}

/**
 * Revision item (counted product)
 */
export interface RevisionItem {
  id: string
  name?: string
  ref?: MockRef
  previousStock: number
  actualStock: number
  difference: number
  purchasePrice?: number
}

/**
 * Revision document (stock count)
 */
export interface Revision {
  id?: string
  ref?: MockRef
  organizationId: string
  store: string
  date: Date | TimestampLike
  dailyStatID: string

  // Items
  items: RevisionItem[]
  totalDifferenceAmount?: number
  totalDifferencePositive?: number
  totalDifferenceNegative?: number

  // Amounts
  amount?: number
  correctedAmount?: number
  negativeAmount?: number
  positiveAmount?: number

  // Status
  isCompleted?: boolean
  status?: string

  // Audit
  cashierUID?: string
  createdAt?: Date | TimestampLike
  comment?: string
}

/**
 * Grouped by date for display
 */
export interface GroupedByDate<T> {
  title: string
  data: T[]
}

interface ApiRevisionResponse {
  id?: string
  firestoreId?: string
  supabaseId?: string
  ref?: { id: string }
  organizationId?: string
  storeId?: string
  storeName?: string
  revisionDate?: string
  dailyStatId?: string
  createdBy?: string
  createdByName?: string
  createdAt?: string
  updatedAt?: string
  amount?: number
  correctedAmount?: number
  negativeAmount?: number
  positiveAmount?: number
  items?: Array<{
    productId?: string
    name?: string
    oldStock?: number
    newStock?: number
    difference?: number
    purchasePrice?: number
    unit?: string
  }>
  deletedAt?: string
  deletedBy?: string
  [key: string]: unknown
}

// ============================================================================
// Helper Functions
// ============================================================================

/** UUID validation regex */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Check if a string is a valid UUID */
const isValidUuid = (value: string | undefined | null): boolean => {
  if (!value || typeof value !== 'string') return false
  return UUID_REGEX.test(value)
}

/** Product dictionary type for stock lookups */
type ProductDict = Record<string, { storeStock?: Record<string, number>; [key: string]: unknown }>

/**
 * Get Jotai store and database reference
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Prepared for future refactoring
const _getDbContext = () => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom) as FirestoreDb
  const organization = jotaiStore.get(organizationAtom) as Organization | null
  const productsDict = jotaiStore.get(productsDictAtom) as ProductDict
  return {
    jotaiStore,
    db,
    organization,
    organizationId: organization?.id,
    productsDict,
  }
}

/**
 * Cast API transfer response to frontend Transfer type.
 * Backend returns camelCase Firestore-format fields.
 * Maps field name differences:
 *   fromStoreId -> fromStore (flat ID string)
 *   toStoreId -> toStore (flat ID string)
 *   transferDate -> date (converted to TimestampLike)
 */
const castApiTransfer = (apiTransfer: TransferResponse): Transfer => {
  return {
    id: apiTransfer.id,
    ref: apiTransfer.ref ? { id: apiTransfer.ref.id || apiTransfer.id } : { id: apiTransfer.id },
    organizationId: apiTransfer.organizationId || '',
    date: convertTimestamp(apiTransfer.transferDate) || convertTimestamp(new Date().toISOString())!,
    dailyStatID: '',
    fromStore: apiTransfer.fromStoreId || '',
    toStore: apiTransfer.toStoreId || '',
    fromStoreName: apiTransfer.fromStoreName ?? undefined,
    toStoreName: apiTransfer.toStoreName ?? undefined,
    items: (apiTransfer.items || []).map(item => ({
      id: item.productId || '',
      name: item.name ?? undefined,
      quantity: item.quantity || 0,
    })),
    totalSum: apiTransfer.amount ?? undefined,
    createdAt: apiTransfer.createdAt ? convertTimestamp(apiTransfer.createdAt) : undefined,
  } as Transfer
}


/**
 * Cast API revision response to frontend Revision type.
 * Backend returns Firestore-era alias fields (store, date, dailyStatID, cashierUID,
 * totalDifferenceAmount, etc.) so no manual mapping is needed.
 * The typed API client auto-converts `date` and `createdAt` to TimestampLike via DATE_FIELDS.
 */
const castApiRevision = (apiRevision: ApiRevisionResponse): Revision => {
  return apiRevision as unknown as Revision
}

/**
 * Group items by date for display
 */
const groupByDate = <T extends { date?: Date | TimestampLike; supplyDate?: Date | TimestampLike; store?: string }>(
  items: T[],
  dateField: 'date' | 'supplyDate',
  storeId?: string
): GroupedByDate<T>[] => {
  const grouped: Record<string, T[]> = {}

  items.forEach((item) => {
    const dateValue = item[dateField]
    const date = dateValue && typeof (dateValue as TimestampLike).toDate === 'function'
      ? (dateValue as TimestampLike).toDate()
      : new Date(dateValue as string | Date)
    const dateKey = format(getLocalStoreDate(item.store || storeId || '', date), 'd/M/y EEEE')

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(item)
  })

  return Object.entries(grouped).map(([title, data]) => ({ title, data }))
}

// ============================================================================
// Supplies
// ============================================================================

/**
 * Get supplies within a date range
 */
export const getSuppliesOnDateRange = async (
  db: unknown,
  range: DateRange | 'allTime',
  flat = false,
  store?: string,
  supplierId?: string,
  unpaidOnly = false
): Promise<Supply[] | GroupedByDate<Supply>[]> => {
  if (shouldUseSupabase()) {
    try {
      const apiParams: Omit<Parameters<typeof suppliesApi.getSuppliesWindow>[0], 'idFrom' | 'idTo'> = {}

      if (store) apiParams.storeId = store
      if (supplierId) apiParams.supplierId = supplierId

      // Only add date params if range has startDate and endDate (not 'allTime')
      if (typeof range === 'object' && range.startDate && range.endDate) {
        apiParams.startDate = range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate as string
        apiParams.endDate = range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate as string
      }

      // ATL-1477: every supply matching the filters, not the newest 50.
      const rawSupplies = await readWholeList(
        (window) => suppliesApi.getSuppliesWindow({ ...apiParams, ...window }),
        { label: 'supplies', order: newestFirst('supplyDate') },
      )
      // Backend returns camelCase Firestore format — just add ref and convert timestamps
      const supplies = (rawSupplies || [])
        .map(supply => transformApiItem(supply as Record<string, unknown>, 'supplies'))
        .filter(Boolean) as unknown as Supply[]

      if (flat) {
        return supplies
      } else {
        return groupByDate(supplies, 'supplyDate', store)
      }
    } catch (error) {
      console.error('Error fetching supplies from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'supplies' },
        extra: { range, store, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation (simplified type assertion)
  const dbRef = db as { collection: (name: string) => unknown }
  let query = dbRef.collection('supplies') as { where: (f: string, o: string, v: unknown) => unknown; get: () => Promise<unknown>; orderBy: (f: string, d: string) => unknown }

  if (store) {
    query = (query as { where: (f: string, o: string, v: unknown) => typeof query }).where('store', '==', store)
  }
  if (supplierId) {
    query = (query as { where: (f: string, o: string, v: unknown) => typeof query }).where('supplierID', '==', supplierId)
  }
  if (unpaidOnly) {
    query = (query as { where: (f: string, o: string, v: unknown) => typeof query }).where('isPaid', '!=', true)
  }

  // Add date range and orderBy only if range has startDate and endDate (not 'allTime')
  let querySnapshot
  if (typeof range === 'object' && range.startDate && range.endDate) {
    querySnapshot = await (query as {
      where: (f: string, o: string, v: unknown) => {
        where: (f: string, o: string, v: unknown) => {
          orderBy: (f: string, d: string) => { get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Supply }) => void) => void }> }
        }
      }
    })
      .where('supplyDate', '>=', range.startDate)
      .where('supplyDate', '<=', range.endDate)
      .orderBy('supplyDate', 'desc')
      .get()
  } else {
    // For 'allTime', skip date filters and orderBy (matches original behavior)
    querySnapshot = await (query as {
      get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Supply }) => void) => void }>
    }).get()
  }

  const supplies: Supply[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Supply }) => {
    const supply = doc.data()
    supply.id = doc.id
    supply.ref = doc.ref
    supplies.push(supply)
  })

  if (flat) {
    return supplies
  } else {
    return groupByDate(supplies, 'supplyDate', store)
  }
}

/**
 * Upload a supply invoice image
 */
export const uploadSupplyInvoice = async (
  file: Blob | string,
  name: string
): Promise<unknown> => {
  reportFirestoreOnlyWriterUsage('uploadSupplyInvoice')
  if (Platform.OS === 'web') {
    return await storage.child('invoices/' + name).put(file as Blob, {
      contentType: 'image/jpeg',
    })
  } else {
    return await (storage.child('invoices/' + name) as { putFile: (f: string, o: { contentType: string }) => Promise<unknown> })
      .putFile(file as string, { contentType: 'image/jpeg' })
  }
}

/**
 * Delete a supply invoice image
 */
export const deleteSupplyInvoice = (name: string): Promise<void> => {
  reportFirestoreOnlyWriterUsage('deleteSupplyInvoice')
  return storage.child('invoices/' + name).delete()
}

// ============================================================================
// Transfers
// ============================================================================

/**
 * Get transfers within a date range
 */
export const getTransfersOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false
): Promise<Transfer[] | GroupedByDate<Transfer>[]> => {
  if (shouldUseSupabase()) {
    try {
      const params = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      // ATL-1477: every transfer in the range, not the newest 50.
      const response = await readWholeList(
        (window) => transfersApi.getTransfersWindow({ ...params, ...window }),
        { label: 'transfers', order: newestFirst('transferDate') },
      )
      const transfers = response.map(castApiTransfer)

      if (flat) {
        return transfers
      } else {
        return groupByDate(transfers, 'date')
      }
    } catch (error) {
      console.error('Error fetching transfers from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'transfers' },
        extra: { range, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const querySnapshot = await (db as {
    collection: (name: string) => {
      where: (f: string, o: string, v: Date) => {
        where: (f: string, o: string, v: Date) => {
          orderBy: (f: string, d: string) => { get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Transfer }) => void) => void }> }
        }
      }
    }
  }).collection('transfers')
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()

  const transfers: Transfer[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Transfer }) => {
    const transfer = doc.data()
    transfer.id = doc.id
    transfer.ref = doc.ref
    transfers.push(transfer)
  })

  if (flat) {
    return transfers
  } else {
    return groupByDate(transfers, 'date')
  }
}

// ============================================================================
// Writeoffs
// ============================================================================

/**
 * Get writeoffs within a date range
 */
export const getWriteoffsOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false,
  store?: string,
  productId?: string
): Promise<Writeoff[] | GroupedByDate<Writeoff>[]> => {
  if (shouldUseSupabase()) {
    try {
      const params: Record<string, unknown> = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      if (store) params.storeId = store
      if (productId) params.productId = productId

      // ATL-1477: every write-off in the range, not the newest 50.
      const data = await readWholeList(
        (window) => writeoffsApi.getWriteoffsWindow({
          storeId: store,
          startDate: params.startDate as string,
          endDate: params.endDate as string,
          ...(productId && { productId }),
          ...window,
        }),
        { label: 'writeoffs', order: newestFirst('date') },
      )
      // Backend returns camelCase Firestore format — just add ref and convert timestamps
      const writeoffs = (data || [])
        .map(writeoff => transformApiItem(writeoff as Record<string, unknown>, 'writeoffs'))
        .filter(Boolean) as unknown as Writeoff[]

      if (flat) {
        return writeoffs
      } else {
        return groupByDate(writeoffs, 'date', store)
      }
    } catch (error) {
      console.error('Error fetching writeoffs from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'writeoffs' },
        extra: { range, store, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  // NOTE: collection ID is case-sensitive — writes go to 'writeOffs' (see createWriteOff below)
  let query = (db as { collection: (name: string) => unknown }).collection('writeOffs') as unknown

  if (store) {
    query = (query as { where: (f: string, o: string, v: unknown) => unknown }).where('store', '==', store)
  }

  const querySnapshot = await (query as {
    where: (f: string, o: string, v: Date) => {
      where: (f: string, o: string, v: Date) => {
        orderBy: (f: string, d: string) => { get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Writeoff }) => void) => void }> }
      }
    }
  })
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()

  const writeoffs: Writeoff[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Writeoff }) => {
    const writeoff = doc.data()
    writeoff.id = doc.id
    writeoff.ref = doc.ref
    writeoffs.push(writeoff)
  })

  if (flat) {
    return writeoffs
  } else {
    return groupByDate(writeoffs, 'date', store)
  }
}

// ============================================================================
// Revisions
// ============================================================================

/**
 * Get revisions within a date range
 */
export const getRevisionsOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false,
  store?: string
): Promise<Revision[] | GroupedByDate<Revision>[]> => {
  if (shouldUseSupabase()) {
    try {
      // ATL-1477: every revision in the range, not the newest 50.
      const response = await readWholeList(
        (window) => revisionsApi.getRevisionsWindow({
          startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate as string,
          endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate as string,
          storeId: store,
          ...window,
        }),
        { label: 'revisions', order: newestFirst('revisionDate') },
      )
      const revisions = (response as unknown as ApiRevisionResponse[]).map(castApiRevision)

      if (flat) {
        return revisions
      } else {
        return groupByDate(revisions, 'date', store)
      }
    } catch (error) {
      console.error('Error fetching revisions from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'revisions' },
        extra: { range, store, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  let query = (db as { collection: (name: string) => unknown }).collection('revisions') as unknown

  if (store) {
    query = (query as { where: (f: string, o: string, v: unknown) => unknown }).where('store', '==', store)
  }

  const querySnapshot = await (query as {
    where: (f: string, o: string, v: Date) => {
      where: (f: string, o: string, v: Date) => {
        orderBy: (f: string, d: string) => { get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Revision }) => void) => void }> }
      }
    }
  })
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()

  const revisions: Revision[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Revision }) => {
    const revision = doc.data()
    revision.id = doc.id
    revision.ref = doc.ref
    revisions.push(revision)
  })

  if (flat) {
    return revisions
  } else {
    return groupByDate(revisions, 'date', store)
  }
}

// ============================================================================
// Write Operations
// ============================================================================

// Note: These functions use 'any' types for compatibility with existing untyped code.
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * ATL-1503 — a supply's payment as a write LEFT it: paid or not, and the Firestore id of the money leg
 * that write booked or kept (`null`: unpaid, or a payment with no leg). A screen that caches the supply
 * (a list item, a detail it will reopen) merges this into its copy, because the NEXT payment update's
 * precondition is read from that copy (`supplyPaymentPrecondition`) — a copy that still says "no leg"
 * after a payment booked one would be refused as stale (409) by the backend.
 */
export type SupplyPaymentState = { isPaid: boolean; transactionId: string | null }

/**
 * ATL-1503 — the cached copy of a supply after a write: the screen's own fields, then the payment
 * state the write reports — so the copy names the leg Firestore now holds, never the one it held before.
 */
export const supplyAfterPaymentWrite = <T extends Record<string, unknown>>(
  cached: T,
  written: Record<string, unknown>,
  payment: SupplyPaymentState,
): T & SupplyPaymentState => ({ ...cached, ...written, ...payment })

/**
 * Save a new supply with stock updates.
 *
 * Returns the new document's ref AND the payment state it was saved with (ATL-1503): the caller
 * caches the supply, and its next payment update's precondition is read from that cache.
 */
export const saveNewSupply = async (
  db: any,
  info: any,
): Promise<{ ref: MockRef; payment: SupplyPaymentState }> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#saveNewSupply')
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>

  // save supply
  const newRef = db.collection('supplies').doc()
  let transactionRef: any

  // Sanitize info for Firestore (remove functions and non-serializable values)
  const sanitizedInfo = sanitizeForFirestoreDeep(info) as Record<string, unknown>

  if (info.createPaymentTransaction) {
    transactionRef = db.collection('transactions').doc()
    batch.set(newRef, { ...sanitizedInfo, transactionId: transactionRef.id })
  } else {
    batch.set(newRef, sanitizedInfo)
  }

  // update stocks
  const storeStockField = 'storeStock.' + info.store
  for (const value of info.items) {
    const increment = value.quantity - value.returnQuantity
    const productRef = getFirestoreRef(value, 'products')
    batch.update(productRef, { [storeStockField]: fieldValue.increment(increment) })
    batch.set(productRef.collection('movement').doc(), {
      productID: value.id,
      type: 'supply',
      previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
      increment,
      date: fieldValue.serverTimestamp(),
      linkedDocId: newRef.id,
      linkedDocDate: info.supplyDate,
      clientSaveDate: new Date(),
      store: info.store,
      organizationId: info.organizationId,
    })
  }

  // update daily stats
  const statDocName = info.dailyStatID
  const statFields: any = {
    suppliesAmount: fieldValue.increment(info.totalSum),
    suppliesQuantity: fieldValue.increment(1),
  }
  if (info.isPaid && info.paymentMethod?.id) {
    statFields[info.paymentMethod.id + 'SuppliesAmount'] = fieldValue.increment(info.totalSum)
    statFields[info.paymentMethod.id + 'SuppliesQuantity'] = fieldValue.increment(1)
    if (info.createPaymentTransaction) {
      batch.set(transactionRef, {
        linkedDocId: newRef.id,
        linkedDocType: 'supply',
        type: 'expense',
        externalDestination: { id: info.supplierID },
        paymentRange: { startDate: info.supplyDate, endDate: info.supplyDate },
        paidBy: info.cashierUID,
        sourceWallet: info.paymentMethod.id,
        value: info.totalSum,
        date: info.paymentDate,
        store: info.store,
        organizationId: info.organizationId,
        createdDate: fieldValue.serverTimestamp(),
      })
    }
  }
  batch.set(db.collection('dailyStats').doc(statDocName), {
    ...statFields,
    store: info.store,
    date: info.supplyDate,
    organizationId: info.organizationId,
  }, { merge: true })

  // update supply order
  if (info.supplyOrderId) {
    batch.update(db.collection('supplyOrders').doc(info.supplyOrderId), {
      supplyId: newRef.id,
      supplyDate: info.supplyDate,
      status: { id: 'completed', text: 'supplyOrders.statusCompleted' },
      isCompleted: true,
    })
  }

  await commitGuardedCreate('inventory.supply.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase — pass-through pattern
  // Backend DTO accepts Firestore field names and transforms internally
  const supplyDto = filterUndefined({
    firestoreId: newRef.id,
    ...sanitizedInfo,
    // ATL-482: coerce supply item fields (numeric barcode → string, Timestamps → ISO) so the raw
    // Firestore product shape doesn't fail the API item schema (items.0.barcode "Invalid input").
    ...(Array.isArray((sanitizedInfo as Record<string, unknown>).items) && {
      items: ((sanitizedInfo as Record<string, unknown>).items as unknown[]).map(coerceCartItemForApi),
    }),
    // Convert Firestore Timestamps to ISO strings for API
    ...(info.supplyDate && {
      supplyDate: info.supplyDate?.toDate ? info.supplyDate.toDate().toISOString() : info.supplyDate instanceof Date ? info.supplyDate.toISOString() : info.supplyDate,
    }),
    ...(info.paymentDate && {
      paymentDate: info.paymentDate?.toDate ? info.paymentDate.toDate().toISOString() : info.paymentDate instanceof Date ? info.paymentDate.toISOString() : info.paymentDate,
    }),
    // paidBy is a UUID column - only send if it looks like a valid UUID
    ...((info.paidBy || info.cashierUID) && isValidUuid(info.paidBy || info.cashierUID) && { paidBy: info.paidBy || info.cashierUID }),
    transactionId: transactionRef?.id,
  } as Record<string, unknown>)

  await bestEffortDualWrite('supplies.create', () =>
    suppliesApi.createSupply(supplyDto as Parameters<typeof suppliesApi.createSupply>[0])
  )

  return {
    ref: newRef,
    payment: { isPaid: Boolean(info.isPaid), transactionId: transactionRef ? transactionRef.id : null },
  }
}

/**
 * ATL-1503 — the store a cached supply was READ from. It decides what a missing leg id means: a till
 * that read Supabase holds the pointer, so its `null` names "the payment behind a NULL pointer"; a till
 * that read Firestore holds the Firestore supply's `transactionId`, so its `null` only says Firestore
 * has no leg. The backend refuses the second against a Supabase supply that has live legs
 * (`supply_payment_precondition_ambiguous`) rather than guess.
 *
 * Decided by the ITEM, never by the org's read flag: the app reads Supabase when the org is cut over,
 * OR this device has the QA override on, OR the local-dev switch is set (`shouldUseSupabase`), and a
 * screen opened by id (`detailId`) re-reads FIRESTORE whatever the mode. `supabaseId` is the marker:
 * the supplies read transform sets it on every item (`SuppliesService.transformSupply`) and the app's
 * API readers keep it (`transformApiItem` spreads the item), while no Firestore supply document carries
 * it — measured 2026-09-29: 0 of 196 organizations' supplies collections (the same query form found
 * `isPaid` in 195 of them).
 */
export const supplyReadSource = (item: { supabaseId?: unknown } | null | undefined): 'firestore' | 'supabase' =>
  typeof item?.supabaseId === 'string' && item.supabaseId !== '' ? 'supabase' : 'firestore'

/**
 * ATL-1503 — the payment a supply showed on this till, as `update_supply_payment_atomic` compares it
 * with the supply's CURRENT payment: paid or not, and for a paid one its money leg as this till holds
 * it — the Firestore transaction id (a till reading Firestore) or the Supabase uuid (a till reading
 * Supabase: the supplies read transform returns the pointer's uuid). `null` = unpaid, or a payment
 * with no leg. A blank id is no id: sending it would be refused as malformed (400) and drop the write.
 * `readFrom` says which store the till read the supply from (`supplyReadSource`).
 */
export const supplyPaymentPrecondition = (
  prevInfo: { isPaid?: unknown; transactionId?: unknown; supabaseId?: unknown } | null | undefined,
): { isPaid: boolean; transactionId: string | null; readFrom: 'firestore' | 'supabase' } => {
  // Truthiness, exactly as updateSupply's own branches read `prevInfo.isPaid` below.
  const isPaid = Boolean(prevInfo?.isPaid)
  const leg = prevInfo?.transactionId
  return {
    isPaid,
    transactionId: isPaid && typeof leg === 'string' && leg.trim() !== '' ? leg : null,
    readFrom: supplyReadSource(prevInfo),
  }
}

/**
 * Update a supply (payment status).
 *
 * Returns the payment state this write left in Firestore (ATL-1503) — see `SupplyPaymentState`. The
 * caller merges it into whatever copy of the supply it caches (`supplyAfterPaymentWrite`).
 */
export const updateSupply = async (
  db: any,
  refOrItem: MockRef | Record<string, unknown>,
  info: any,
  prevInfo: any,
): Promise<SupplyPaymentState> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#updateSupply')
  const batch = rootDb.batch()
  // Accepts the whole supply object (works when `.ref` was stripped; ATL-548 pattern)
  const firestoreRef = getFirestoreRef(refOrItem, 'supplies')
  const supplyId: string = firestoreRef.id
  let statDocName = ''
  const statFields: any = {}
  let transactionRef: any = null

  if (info.isPaid && !prevInfo.isPaid) {
    if (info.createPaymentTransaction) {
      transactionRef = db.collection('transactions').doc()
      batch.update(firestoreRef, { ...info, transactionId: transactionRef.id })
    } else {
      batch.update(firestoreRef, info)
    }
    // ATL-1013: `info.paymentDailyStatID` comes straight off the payment form, which computes
    // it with `getDailyStatID(null, isPaid ? paymentDate : null)`. That has TWO failure modes,
    // not one: it returns null when no store resolves (→ `doc(null)`, which throws and loses
    // the whole payment, since `statDocName !== ''` waves null through); and when a store DOES
    // resolve but the date is null, the `date = new Date()` default does NOT fire — defaults
    // only fire on `undefined` — so `new Date(null)` yields the epoch and the id is a truthy,
    // meaningless `1-1-1970-<store>`. Unreachable from this branch today (both legs are gated
    // on isPaid), but this PR makes "the stored id always wins" load-bearing, so a future
    // reader of `paymentDailyStatID` outside that gate would post supply money into 1970.
    // Tracked with the screen fix in ATL-1016. Key it on the supply's own business day when
    // the form's value is missing.
    statDocName = dailyStatIDForRecord(info.paymentDailyStatID, prevInfo.store, info.paymentDate || prevInfo.supplyDate)
    if (info.paymentMethod?.id) {
      statFields[info.paymentMethod.id + 'SuppliesAmount'] = fieldValue.increment(prevInfo.totalSum)
      statFields[info.paymentMethod.id + 'SuppliesQuantity'] = fieldValue.increment(1)
    }
    if (info.createPaymentTransaction) {
      batch.set(transactionRef, {
        linkedDocId: supplyId,
        linkedDocType: 'supply',
        type: 'expense',
        externalDestination: { id: prevInfo.supplierID },
        paymentRange: {
          startDate: prevInfo.supplyDate?.toDate ? prevInfo.supplyDate.toDate() : prevInfo.supplyDate,
          endDate: prevInfo.supplyDate?.toDate ? prevInfo.supplyDate.toDate() : prevInfo.supplyDate,
        },
        paidBy: info.paidBy,
        sourceWallet: info.paymentMethod?.id,
        value: prevInfo.totalSum,
        date: info.paymentDate,
        store: prevInfo.store,
        organizationId: prevInfo.organizationId || info.organizationId,
        createdDate: fieldValue.serverTimestamp(),
      })
    }
  }
  if (!info.isPaid && prevInfo.isPaid) {
    batch.update(firestoreRef, { ...info, transactionId: null })
    // ATL-1013: business day, not the device's calendar day — see deleteSale. Which STORED
    // id is preferred is deliberately left as it was: `paymentDailyStatID` is recomputed by
    // the payment form on every save, including saves that move no money, so it does not
    // reliably name the doc the counters are in — while `saveNewSupply` puts a paid-at-
    // creation amount in the SUPPLY-day doc (inventory.ts:784). Preferring it here would
    // need the writer fixed and the 106+14 already-wrong rows backfilled: ATL-1016, not this.
    statDocName = dailyStatIDForRecord(prevInfo.dailyStatID, prevInfo.store, prevInfo.paymentDate || prevInfo.supplyDate)
    statFields[prevInfo.paymentMethod.id + 'SuppliesAmount'] = fieldValue.increment(-prevInfo.totalSum)
    statFields[prevInfo.paymentMethod.id + 'SuppliesQuantity'] = fieldValue.increment(-1)
    if (prevInfo.transactionId) {
      batch.delete(db.collection('transactions').doc(prevInfo.transactionId))
    }
  }
  if (info.isPaid && prevInfo.isPaid && info.paymentMethod?.id !== prevInfo.paymentMethod?.id) {
    // ATL-1013: business day, not the device's calendar day — see deleteSale. Which STORED
    // id is preferred is deliberately left as it was: `paymentDailyStatID` is recomputed by
    // the payment form on every save, including saves that move no money, so it does not
    // reliably name the doc the counters are in — while `saveNewSupply` puts a paid-at-
    // creation amount in the SUPPLY-day doc (inventory.ts:784). Preferring it here would
    // need the writer fixed and the 106+14 already-wrong rows backfilled: ATL-1016, not this.
    statDocName = dailyStatIDForRecord(prevInfo.dailyStatID, prevInfo.store, prevInfo.paymentDate || prevInfo.supplyDate)
    if (info.paymentMethod?.id) {
      statFields[info.paymentMethod.id + 'SuppliesAmount'] = fieldValue.increment(prevInfo.totalSum)
      statFields[info.paymentMethod.id + 'SuppliesQuantity'] = fieldValue.increment(1)
    }
    if (prevInfo.paymentMethod?.id) {
      statFields[prevInfo.paymentMethod.id + 'SuppliesAmount'] = fieldValue.increment(-prevInfo.totalSum)
      statFields[prevInfo.paymentMethod.id + 'SuppliesQuantity'] = fieldValue.increment(-1)
    }
    if (prevInfo.transactionId) {
      batch.update(db.collection('transactions').doc(prevInfo.transactionId), {
        sourceWallet: info.paymentMethod?.id,
      })
    }
  }
  if (statDocName !== '') {
    batch.update(db.collection('dailyStats').doc(statDocName), statFields)
  }
  await batch.commit()

  // Dual write to Supabase — pass through info directly (backend DTO handles field selection)
  const updateDto = filterUndefined({
    ...info,
    // ATL-482: coerce supply item fields (numeric barcode → string, Timestamps → ISO) for the API item schema.
    ...(Array.isArray(info.items) && { items: (info.items as unknown[]).map(coerceCartItemForApi) }),
    // Convert Firestore Timestamps to ISO strings
    paymentDate: info.paymentDate?.toDate ? info.paymentDate.toDate().toISOString() : info.paymentDate instanceof Date ? info.paymentDate.toISOString() : info.paymentDate,
    supplyDate: info.supplyDate?.toDate ? info.supplyDate.toDate().toISOString() : info.supplyDate instanceof Date ? info.supplyDate.toISOString() : info.supplyDate,
    // Normalize field name (Firestore uses supplierID, backend accepts both)
    supplierId: info.supplierID || info.supplierId,
    // Pass Firestore transaction ID so Supabase transaction gets firestore_id
    transactionId: transactionRef?.id,
    // ATL-1503: the payment state this update was made FROM — what this till showed (`prevInfo`).
    // The backend refuses (409, permanent) an update whose precondition is not the supply's current
    // payment, so a delayed unpay (another till's outbox, a retry after a lost ack) cannot void a
    // later payment, a stale unpaid save cannot void one it never saw, and an edit of a payment
    // voided since cannot book a new one. The precondition is only honest if this till's own writes
    // reach the backend IN ORDER — each is made from the state the previous one left. It travels
    // inside the payload, and the outbox keeps the chain intact: an entry carrying it is never
    // replaced or purged by a later write of the supply, and this write is queued BEHIND any entry
    // the supply still has instead of being sent past it (`queueBehindDocument`; see
    // `isPreconditionedRequest` in dualWriteOutbox.ts).
    ...(info.isPaid !== undefined && { expectedPayment: supplyPaymentPrecondition(prevInfo) }),
  } as Record<string, unknown>)
  const preconditioned = info.isPaid !== undefined

  await bestEffortDualWrite(
    'supplies.update',
    () => suppliesApi.updateSupply(supplyId, updateDto as Parameters<typeof suppliesApi.updateSupply>[1]),
    preconditioned
      ? {
          firestoreId: supplyId,
          queueBehindDocument: () =>
            captureWriteRequest((client) =>
              createSuppliesApi(client).updateSupply(supplyId, updateDto as Parameters<typeof suppliesApi.updateSupply>[1]),
            ),
        }
      : undefined,
  )

  return supplyPaymentStateAfterUpdate(info, prevInfo, transactionRef?.id ?? null)
}

/**
 * ATL-1503 — what `updateSupply`'s Firestore batch left as the supply's payment, branch by branch:
 *  - became paid: the leg it booked (`transactionId`), or none (`createPaymentTransaction` off — an
 *    unpaid supply carries no leg, and this branch writes none);
 *  - became unpaid: no leg (the batch writes `transactionId: null`);
 *  - paid → paid, or no payment change: the leg the supply already had (the batch leaves it).
 */
const supplyPaymentStateAfterUpdate = (
  info: { isPaid?: unknown },
  prevInfo: { isPaid?: unknown; transactionId?: unknown },
  bookedLeg: string | null,
): SupplyPaymentState => {
  const wasPaid = Boolean(prevInfo?.isPaid)
  const isPaid = info.isPaid === undefined ? wasPaid : Boolean(info.isPaid)
  if (isPaid && !wasPaid) return { isPaid: true, transactionId: bookedLeg }
  if (!isPaid) return { isPaid: false, transactionId: null }
  const { transactionId } = supplyPaymentPrecondition(prevInfo)
  return { isPaid: true, transactionId }
}

/**
 * Delete a supply with stock reversal
 */
export const deleteSupply = async (db: any, refOrItem: MockRef | Record<string, unknown>, info: any): Promise<void> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#deleteSupply')
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
  // Accepts the whole supply object (works when `.ref` was stripped; ATL-548 pattern)
  const firestoreRef = getFirestoreRef(refOrItem ?? info, 'supplies')
  const supplyId: string = firestoreRef.id

  // Idempotent transaction: a frozen UI / double-tap / offline replay / retry must not
  // re-apply this reversal, or stock double-subtracts (ATL — VSH/Усач hit -645).
  // runIdempotentDelete deletes the supply doc and runs the reversal below EXACTLY once.
  await runIdempotentDelete(firestoreRef, 'deleteSupply', (tx) => {
    // update stocks
    const storeStockField = 'storeStock.' + info.store
    for (const value of info.items) {
      if (allProductsDict[value.id]) {
        const increment = value.returnQuantity - value.quantity
        const productRef = getFirestoreRef(value, 'products')
        tx.update(productRef, { [storeStockField]: fieldValue.increment(increment) })
        tx.set(productRef.collection('movement').doc(), {
          productID: value.id,
          type: 'supply_reversal',
          previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: supplyId,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId,
        })
      }
    }

    // update stats
    // ATL-1013: business day, not the device's calendar day — see deleteSale.
    const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.supplyDate)
    tx.update(db.collection('dailyStats').doc(statDocName), {
      suppliesAmount: fieldValue.increment(-info.totalSum),
      suppliesQuantity: fieldValue.increment(-1),
    })

    // update payment stats
    if (info.isPaid) {
      const paymentStatDocName = dailyStatIDForRecord(info.paymentDailyStatID || info.dailyStatID, info.store, info.paymentDate || info.supplyDate)
      if (info.paymentMethod?.id != null) {
        tx.update(db.collection('dailyStats').doc(paymentStatDocName), {
          [info.paymentMethod.id + 'SuppliesAmount']: fieldValue.increment(-info.totalSum),
        })
        if (info.transactionId) {
          tx.delete(db.collection('transactions').doc(info.transactionId))
        }
      }
    }

    // update supply order
    if (info.supplyOrderId) {
      tx.update(db.collection('supplyOrders').doc(info.supplyOrderId), {
        supplyId: null,
        supplyDate: null,
      })
    }
  })

  await bestEffortDualWriteDelete('supplies.delete', () => suppliesApi.deleteSupply(supplyId), { firestoreId: supplyId })
}

/**
 * Save a new writeoff with stock updates
 */
export const saveNewWriteOff = async (db: any, info: any): Promise<MockRef> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#saveNewWriteOff')
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>

  const ref = db.collection('writeOffs').doc()
  // Sanitize info to convert any Timestamps to Dates before saving
  const sanitizedInfo = sanitizeForFirestoreDeep(info)
  batch.set(ref, sanitizedInfo)

  // update stocks
  const storeStockField = 'storeStock.' + info.store
  if (info.product.isComposite && !info.product.manufacturedBeforeSale) {
    for (const ingredient of info.product.ingredients) {
      if (!allProductsDict[ingredient.id]) {
        const error: any = new Error('INGREDIENT_NOT_FOUND')
        error.ingredientName = ingredient.name
        error.productName = info.product.name
        throw error
      }
      const increment = 0 - (info.quantity * ingredient.quantity)
      const ingredientRef = getFirestoreRef(ingredient, 'products')
      batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
      batch.set(ingredientRef.collection('movement').doc(), {
        productID: ingredient.id,
        type: 'writeOff',
        previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
        increment,
        date: fieldValue.serverTimestamp(),
        linkedDocId: ref.id,
        linkedDocDate: info.date,
        clientSaveDate: new Date(),
        store: info.store,
        organizationId: info.organizationId,
      })
    }
  } else {
    const increment = 0 - info.quantity
    const productRef = getFirestoreRef(info.product, 'products')
    batch.update(productRef, { [storeStockField]: fieldValue.increment(increment) })
    batch.set(productRef.collection('movement').doc(), {
      productID: info.product.id,
      type: 'writeOff',
      previousInStock: allProductsDict[info.product.id]?.storeStock?.[info.store] || 0,
      increment,
      date: fieldValue.serverTimestamp(),
      linkedDocId: ref.id,
      linkedDocDate: info.date,
      clientSaveDate: new Date(),
      store: info.store,
      organizationId: info.organizationId,
    })
  }

  // update daily stats
  const statDocName = info.dailyStatID
  batch.set(db.collection('dailyStats').doc(statDocName), {
    writeOffsAmount: fieldValue.increment(info.totalSum),
    writeOffsQuantity: fieldValue.increment(1),
    date: info.date,
    store: info.store,
    organizationId: info.organizationId,
  }, { merge: true })

  await commitGuardedCreate('inventory.writeOff.create', db, ref, batch) // ATL-2004: a re-delivery cannot apply twice

  // Build items array — expand composite products into ingredients
  // to match Firestore stock logic (ingredients get decremented, not the composite)
  const reasonValue = info.reason?.id || info.reason
  let writeoffItems: Array<{ productId: string; quantity: number; reason?: string; name?: string; amount?: number; purchasePrice?: number }>
  if (info.product.isComposite && !info.product.manufacturedBeforeSale) {
    writeoffItems = info.product.ingredients.map((ingredient: any) => ({
      productId: ingredient.id,
      quantity: info.quantity * ingredient.quantity,
      reason: reasonValue,
      name: ingredient.name,
      amount: (ingredient.purchasePrice || 0) * info.quantity * ingredient.quantity,
      purchasePrice: ingredient.purchasePrice || 0,
    }))
  } else {
    writeoffItems = [{
      productId: info.product.id,
      quantity: info.quantity,
      reason: reasonValue,
      name: info.product.name,
      amount: (info.product.purchasePrice || 0) * info.quantity,
      purchasePrice: info.product.purchasePrice || 0,
    }]
  }

  await bestEffortDualWrite('writeoffs.create', () =>
    writeoffsApi.createWriteoff({
      firestoreId: ref.id,
      organizationId: info.organizationId,
      storeId: info.store,
      totalSum: info.totalSum,
      items: writeoffItems,
      dailyStatId: statDocName,
      ...(info.date && {
        writeoffDate: info.date?.toDate ? info.date.toDate().toISOString() : info.date instanceof Date ? info.date.toISOString() : info.date,
      }),
    } as Parameters<typeof writeoffsApi.createWriteoff>[0])
  )

  return ref
}

/**
 * Delete a writeoff with stock reversal
 */
export const deleteWriteOff = async (db: any, refOrItem: MockRef | Record<string, unknown>, info: any): Promise<void> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#deleteWriteOff')
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
  // Accepts the whole writeoff object (works when `.ref` was stripped; ATL-548 pattern)
  const firestoreRef = getFirestoreRef(refOrItem ?? info, 'writeOffs')
  const writeoffId: string = firestoreRef.id

  // Idempotent transaction (see runIdempotentDelete): a retried/double-fired writeoff delete
  // must not add the stock back more than once.
  await runIdempotentDelete(firestoreRef, 'deleteWriteOff', (tx) => {
    // update stocks
    const storeStockField = 'storeStock.' + info.store
    if (info.product.isComposite && !info.product.manufacturedBeforeSale) {
      for (const ingredient of info.product.ingredients) {
        if (allProductsDict[ingredient.id]) {
          const increment = info.quantity * ingredient.quantity
          const ingredientRef = getFirestoreRef(ingredient, 'products')
          tx.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
          tx.set(ingredientRef.collection('movement').doc(), {
            productID: ingredient.id,
            type: 'deleteWriteOff',
            previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: writeoffId,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: info.store,
            organizationId: info.organizationId,
          })
        }
      }
    } else {
      if (allProductsDict[info.product.id]) {
        const increment = info.quantity
        const productRef = getFirestoreRef(info.product, 'products')
        tx.update(productRef, { [storeStockField]: fieldValue.increment(increment) })
        tx.set(productRef.collection('movement').doc(), {
          productID: info.product.id,
          type: 'deleteWriteOff',
          previousInStock: allProductsDict[info.product.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: writeoffId,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId,
        })
      }
    }

    // update daily stats
    // ATL-1013: business day, not the device's calendar day — see deleteSale.
    const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.date)
    tx.update(db.collection('dailyStats').doc(statDocName), {
      writeOffsAmount: fieldValue.increment(-info.totalSum),
      writeOffsQuantity: fieldValue.increment(-1),
    })
  })

  await bestEffortDualWriteDelete('writeoffs.delete', () => writeoffsApi.deleteWriteoff(writeoffId), { firestoreId: writeoffId })
}

/**
 * Save a new revision with stock updates
 */
export const saveNewRevision = async (db: any, info: any): Promise<MockRef> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#saveNewRevision')
  const batch = rootDb.batch()

  const ref = db.collection('revisions').doc()
  // Sanitize before persisting: info.items carry live native Firestore product refs
  // (RevisionDetailScreen sets them), which are cyclical on iOS and crash any later
  // JSON.stringify ("cyclical structure in JSON object"). Strips `ref` like every other
  // write here; the original `info` is kept intact below for getFirestoreRef/stat math.
  batch.set(ref, sanitizeForFirestoreDeep(info) as Record<string, unknown>)

  // update stocks
  const storeStockField = 'storeStock.' + info.store
  for (const value of info.items) {
    const productRef = getFirestoreRef(value, 'products')
    batch.update(productRef, { [storeStockField]: value.inStock })
    batch.set(productRef.collection('movement').doc(), {
      productID: value.id,
      type: 'revision',
      previousInStock: value.inStockOld || 0,
      increment: value.inStock - value.inStockOld,
      date: fieldValue.serverTimestamp(),
      linkedDocId: ref.id,
      linkedDocDate: info.date,
      clientSaveDate: new Date(),
      store: info.store,
      organizationId: info.organizationId,
    })
  }

  // update daily stats
  batch.set(db.collection('dailyStats').doc(info.dailyStatID), {
    revisionsAmount: fieldValue.increment(info.amount),
    correctedRevisionsAmount: fieldValue.increment(info.correctedAmount),
    negativeRevisionsAmount: fieldValue.increment(info.negativeAmount),
    positiveRevisionsAmount: fieldValue.increment(info.positiveAmount),
    revisionsQuantity: fieldValue.increment(1),
    date: info.date,
    store: info.store,
    organizationId: info.organizationId,
  }, { merge: true })

  try {
    await commitGuardedCreate('inventory.revision.create', db, ref, batch) // ATL-2004: a re-delivery cannot apply twice
  } catch (error) {
    // ATL-1793: one counted product whose Firestore document is gone makes the server refuse the
    // whole count (`update` needs the document). Name it, so the screen can say which row to leave
    // out instead of a bare «Ошибка» that no retry can clear.
    throw await explainRefusedCommit('saveNewRevision', error, info.items)
  }

  await bestEffortDualWrite('revisions.create', () =>
    revisionsApi.createRevision({
      firestoreId: ref.id,
      organizationId: info.organizationId,
      storeId: info.store,
      amount: info.amount,
      correctedAmount: info.correctedAmount,
      negativeAmount: info.negativeAmount,
      positiveAmount: info.positiveAmount,
      items: info.items.map((item: any) => ({
        productId: item.id,
        name: item.name,
        oldStock: item.inStockOld || 0,
        newStock: item.inStock,
        purchasePrice: item.purchasePrice,
        // ATL-1305: the Firestore document carries these and this body did not, so a revision
        // created on a cut-over organization had no per-item money on the Supabase side at all —
        // RevisionStats totalled `undefined` and rendered «не число». Kept conditional so the
        // stored shape stays equal to what saveNewRevision writes to Firestore (both are omitted
        // there when unset), and so an absent correction is not sent as an explicit null.
        ...(item.individualAmount != null && { individualAmount: item.individualAmount }),
        ...(item.correctedIndividualAmount != null && {
          correctedIndividualAmount: item.correctedIndividualAmount,
        }),
        ...(item.correctionReason != null && { correctionReason: item.correctionReason }),
      })),
      dailyStatId: info.dailyStatID,
      revisionDate: info.date,
    })
  )

  return ref
}

/**
 * Update a revision
 */
export const updateRevision = async (db: any, updatedInfo: any, oldInfo: any): Promise<void> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#updateRevision')
  const batch = rootDb.batch()
  // updatedInfo.items are copied from a loaded revision and carry live native product refs
  // (cyclical on iOS). Strip them once and reuse the clean copy for BOTH the native write and
  // the dual-write body below — the typed client's serializeDates has no cycle guard (ATL-497),
  // so correctness must not depend on every caller having pre-stripped refs.
  const sanitizedUpdate = sanitizeForFirestoreDeep(updatedInfo) as Record<string, unknown>
  // Resolve via the whole object — oldInfo.ref may be stripped (ATL-548 pattern)
  const revisionRef = getFirestoreRef(oldInfo, 'revisions')
  batch.update(revisionRef, sanitizedUpdate)

  // update daily stats
  const difference = updatedInfo.correctedAmount - (oldInfo.correctedAmount || 0)
  // ATL-1013: business day, not the device's calendar day — see deleteSale.
  const statDocName = dailyStatIDForRecord(oldInfo.dailyStatID, oldInfo.store, oldInfo.date)
  batch.update(db.collection('dailyStats').doc(statDocName), {
    correctedRevisionsAmount: fieldValue.increment(difference),
  })

  await batch.commit()

  // Map Firestore-era item spellings to the API contract, symmetric with createRevision
  // (ATL-535): the generated request schema REQUIRES items[].oldStock/newStock, and the
  // backend UPDATE normalizer reads only oldStock/newStock (unlike create, it ignores
  // inStock/inStockOld) — so raw Firestore-read items {inStock, inStockOld} fail request
  // validation client-side AND would zero out stocks server-side.
  const updateItems = (sanitizedUpdate as { items?: unknown }).items
  const updateDto = {
    ...sanitizedUpdate,
    ...(Array.isArray(updateItems) && {
      items: (updateItems as Record<string, unknown>[]).map((item) => ({
        productId: item.id ?? item.productId,
        name: item.name,
        // ?? 0 like createRevision: oldStock/newStock are REQUIRED z.number() in the schema —
        // a missing/null stock must not fail validation (the backend defaults to 0 anyway).
        oldStock: item.inStockOld ?? item.oldStock ?? 0,
        newStock: item.inStock ?? item.newStock ?? 0,
        purchasePrice: item.purchasePrice,
        // ATL-1305: an update REPLACES revisions.items wholesale on the Supabase side, so every
        // field this map omits is deleted from the stored row. Applying a correction — the only
        // thing this screen's edit mode does — used to erase the amounts it was correcting.
        ...(item.individualAmount != null && { individualAmount: item.individualAmount }),
        ...(item.correctedIndividualAmount != null && {
          correctedIndividualAmount: item.correctedIndividualAmount,
        }),
        ...(item.correctionReason != null && { correctionReason: item.correctionReason }),
      })),
    }),
  }
  await bestEffortDualWrite('revisions.update', () =>
    revisionsApi.updateRevision(
      revisionRef.id,
      updateDto as Parameters<typeof revisionsApi.updateRevision>[1],
    )
  )
}

/**
 * Delete a revision with stock reversal
 */
export const deleteRevision = async (db: any, info: any): Promise<void> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#deleteRevision')
  // ATL-1796: the reversal below writes `storeStock.<info.store>`. Without a real Firestore store id
  // that is `storeStock.null` / `storeStock.placeholder_…` — a key no store has — and the counted
  // stock is never reverted. The backend answers `store: null` when the store has no Firestore twin,
  // so refuse before any write rather than delete the revision with its effect still applied.
  if (typeof info?.store !== 'string' || info.store === '' || info.store.startsWith('placeholder_')) {
    throw new Error(`deleteRevision: revision ${info?.firestoreId ?? info?.id} has no Firestore store id (${String(info?.store)}); refusing to reverse its stock`)
  }
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
  const revisionRef = getFirestoreRef(info, 'revisions')

  // Idempotent transaction (see runIdempotentDelete): a retried/double-fired revision delete
  // must not re-apply the stock delta more than once.
  await runIdempotentDelete(revisionRef, 'deleteRevision', (tx) => {
    // update stocks
    const storeStockField = 'storeStock.' + info.store
    for (const value of info.items) {
      if (allProductsDict[value.id]) {
        const productRef = getFirestoreRef(value, 'products')
        tx.update(productRef, { [storeStockField]: fieldValue.increment(-(value.inStock - value.inStockOld)) })
        tx.set(productRef.collection('movement').doc(), {
          productID: value.id,
          type: 'deleteRevision',
          previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
          increment: -(value.inStock - value.inStockOld),
          date: fieldValue.serverTimestamp(),
          linkedDocId: revisionRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId,
        })
      }
    }

    // update daily stats
    // ATL-1013: `updateRevision` above resolves this through the shared helper; this
    // reversal did not. A revision opened from the operations list (or read back from
    // Supabase) carries no `dailyStatID`, so `doc(undefined)` threw inside the transaction
    // and the revision could never be deleted at all.
    const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.date)
    tx.update(db.collection('dailyStats').doc(statDocName), {
      revisionsAmount: fieldValue.increment(-info.amount),
      correctedRevisionsAmount: fieldValue.increment(-info.correctedAmount),
      negativeRevisionsAmount: fieldValue.increment(-info.negativeAmount),
      positiveRevisionsAmount: fieldValue.increment(-info.positiveAmount),
      revisionsQuantity: fieldValue.increment(-1),
    })
  })

  await bestEffortDualWriteDelete('revisions.delete', () => revisionsApi.deleteRevision(revisionRef.id), { firestoreId: revisionRef.id })
}

/**
 * Save a new transfer with stock updates
 */
export const saveNewTransfer = async (db: any, info: any): Promise<MockRef> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#saveNewTransfer')
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>

  const ref = db.collection('transfers').doc()
  // Sanitize info to convert Firestore Timestamps to ISO strings and remove functions
  const sanitizedInfo = sanitizeForFirestoreDeep(info)
  batch.set(ref, sanitizedInfo)

  // update stocks
  for (const product of info.items) {
    if (product.isComposite && !product.manufacturedBeforeSale) {
      for (const ingredient of product.ingredients) {
        const fromStoreStockField = 'storeStock.' + info.fromStore.id
        const fromIncrement = 0 - (product.quantity * ingredient.quantity)
        const toStoreStockField = 'storeStock.' + info.toStore.id
        const toIncrement = product.quantity * ingredient.quantity
        const ingredientRef = getFirestoreRef(ingredient, 'products')
        batch.update(ingredientRef, {
          [fromStoreStockField]: fieldValue.increment(fromIncrement),
          [toStoreStockField]: fieldValue.increment(toIncrement),
        })
        batch.set(ingredientRef.collection('movement').doc(), {
          productID: ingredient.id,
          type: 'transfer',
          previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.fromStore.id] || 0,
          increment: fromIncrement,
          date: fieldValue.serverTimestamp(),
          linkedDocId: ref.id,
          linkedDocDate: info.date,
          clientSaveDate: new Date(),
          store: info.fromStore.id,
          organizationId: info.organizationId,
        })
        batch.set(ingredientRef.collection('movement').doc(), {
          productID: ingredient.id,
          type: 'transfer',
          previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.toStore.id] || 0,
          increment: toIncrement,
          date: fieldValue.serverTimestamp(),
          linkedDocId: ref.id,
          linkedDocDate: info.date,
          clientSaveDate: new Date(),
          store: info.toStore.id,
          organizationId: info.organizationId,
        })
      }
    } else {
      const fromStoreStockField = 'storeStock.' + info.fromStore.id
      const fromIncrement = 0 - product.quantity
      const toStoreStockField = 'storeStock.' + info.toStore.id
      const toIncrement = product.quantity
      const productRef = getFirestoreRef(product, 'products')
      batch.update(productRef, {
        [fromStoreStockField]: fieldValue.increment(fromIncrement),
        [toStoreStockField]: fieldValue.increment(toIncrement),
      })
      batch.set(productRef.collection('movement').doc(), {
        productID: product.id,
        type: 'transfer',
        previousInStock: allProductsDict[product.id]?.storeStock?.[info.fromStore.id] || 0,
        increment: fromIncrement,
        date: fieldValue.serverTimestamp(),
        linkedDocId: ref.id,
        linkedDocDate: info.date,
        clientSaveDate: new Date(),
        store: info.fromStore.id,
        organizationId: info.organizationId,
      })
      batch.set(productRef.collection('movement').doc(), {
        productID: product.id,
        type: 'transfer',
        previousInStock: allProductsDict[product.id]?.storeStock?.[info.toStore.id] || 0,
        increment: toIncrement,
        date: fieldValue.serverTimestamp(),
        linkedDocId: ref.id,
        linkedDocDate: info.date,
        clientSaveDate: new Date(),
        store: info.toStore.id,
        organizationId: info.organizationId,
      })
    }
  }

  // update daily stats
  batch.set(db.collection('dailyStats').doc(info.fromDailyStatID), {
    transfersAmount: fieldValue.increment(-info.amount),
    date: info.date,
    store: info.fromStore.id,
    organizationId: info.organizationId,
  }, { merge: true })
  batch.set(db.collection('dailyStats').doc(info.toDailyStatID), {
    transfersAmount: fieldValue.increment(info.amount),
    date: info.date,
    store: info.toStore.id,
    organizationId: info.organizationId,
  }, { merge: true })

  await commitGuardedCreate('inventory.transfer.create', db, ref, batch) // ATL-2004: a re-delivery cannot apply twice

  await bestEffortDualWrite('transfers.create', () =>
    transfersApi.createTransfer({
      firestoreId: ref.id,
      organizationId: info.organizationId,
      fromStoreId: info.fromStore.id,
      toStoreId: info.toStore.id,
      amount: info.amount,
      // ATL-768: expand composite lines into their INGREDIENTS, with the SAME predicate and
      // the SAME quantity expression as the Firestore stock branch above — one predicate, so
      // there is no falsy-vs-strict transcription hazard (the ATL-766 class), mirroring how
      // saveNewWriteOff builds writeoffItems. Without this the payload sent the composite
      // un-expanded and Supabase moved the COMPOSITE on both stores while Firestore moved
      // the ingredients — four one-sided pairs per line. The backend/RPC stay deliberately
      // composite-ignorant (pinned: transfers-know-nothing-about-composites); the expansion
      // is the frontend's job on this path, exactly like Firestore's own write. deleteTransfer
      // needs no twin fix: delete_transfer_atomic reverses the movements create recorded, so
      // symmetric ingredient movements reverse symmetrically.
      items: info.items.flatMap((item: any) =>
        item.isComposite && !item.manufacturedBeforeSale
          ? item.ingredients.map((ingredient: any) => ({
              productId: ingredient.id,
              quantity: item.quantity * ingredient.quantity,
              name: ingredient.name,
            }))
          : [{
              productId: item.id,
              quantity: item.quantity,
              name: item.name,
            }]
      ),
      fromDailyStatId: info.fromDailyStatID,
      toDailyStatId: info.toDailyStatID,
      ...(info.date && { transferDate: info.date instanceof Date ? info.date.toISOString() : info.date }),
    } as Parameters<typeof transfersApi.createTransfer>[0])
  )

  return ref
}

/**
 * Delete a transfer with stock reversal
 */
export const deleteTransfer = async (db: any, info: any): Promise<void> => {
  assertWWriterAvailable('global/Firebase/inventory.ts#deleteTransfer')
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>

  const transferRef = getFirestoreRef(info, 'transfers')

  // Transfer items loaded from the operations list under Supabase mode carry
  // only {name, quantity, productId} where productId is the product's SUPABASE
  // UUID (not its Firestore id) — so getFirestoreRef(product, 'products') can't
  // resolve them and throws BEFORE the Supabase cleanup runs, leaving the
  // transfer un-reversed. Re-hydrate each item from the live productsDict
  // (keyed by Firestore product id). Because the item's productId is a UUID, we
  // build a reverse index over every id spelling a product object can carry so
  // a Firestore-id, supabaseId or UUID all map back to the full product (which
  // has the Firestore id, ref, isComposite/ingredients and storeStock).
  const productByAnyId: Record<string, any> = {}
  for (const p of Object.values(allProductsDict)) {
    for (const key of [p?.id, p?.firestoreId, p?.firestore_id, p?.supabaseId, p?.supabase_id]) {
      if (key) productByAnyId[key as string] = p
    }
  }
  const resolveItem = (item: any): any => {
    const pid =
      item.id || item.firestoreId || item.firestore_id || item.productId || item.supabaseId
    const full = pid ? productByAnyId[pid] : undefined
    return full ? { ...full, ...item, id: full.id, ref: full.ref } : item
  }
  const items: any[] = (info.items || []).map(resolveItem)

  // Idempotent transaction (see runIdempotentDelete): a transfer moves stock between two
  // stores, so a retried/double-fired delete would reverse it more than once and corrupt
  // BOTH stores' balances. Reverse exactly once.
  await runIdempotentDelete(transferRef, 'deleteTransfer', (tx) => {
    // update stocks
    for (const product of items) {
      if (product.isComposite && !product.manufacturedBeforeSale) {
        for (const ingredient of product.ingredients) {
          const fromStoreStockField = 'storeStock.' + info.fromStore.id
          const fromIncrement = 0 - (product.quantity * ingredient.quantity)
          const toStoreStockField = 'storeStock.' + info.toStore.id
          const toIncrement = product.quantity * ingredient.quantity
          const ingredientRef = getFirestoreRef(ingredient, 'products')
          tx.update(ingredientRef, {
            [fromStoreStockField]: fieldValue.increment(-fromIncrement),
            [toStoreStockField]: fieldValue.increment(-toIncrement),
          })
          tx.set(ingredientRef.collection('movement').doc(), {
            productID: ingredient.id,
            type: 'deleteTransfer',
            previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.fromStore.id] || 0,
            increment: -fromIncrement,
            date: fieldValue.serverTimestamp(),
            linkedDocId: transferRef.id,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: info.fromStore.id,
            organizationId: info.organizationId,
          })
          tx.set(ingredientRef.collection('movement').doc(), {
            productID: ingredient.id,
            type: 'deleteTransfer',
            previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.toStore.id] || 0,
            increment: -toIncrement,
            date: fieldValue.serverTimestamp(),
            linkedDocId: transferRef.id,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: info.toStore.id,
            organizationId: info.organizationId,
          })
        }
      } else {
        const fromStoreStockField = 'storeStock.' + info.fromStore.id
        const fromIncrement = 0 - product.quantity
        const toStoreStockField = 'storeStock.' + info.toStore.id
        const toIncrement = product.quantity
        const productRef = getFirestoreRef(product, 'products')
        tx.update(productRef, {
          [fromStoreStockField]: fieldValue.increment(-fromIncrement),
          [toStoreStockField]: fieldValue.increment(-toIncrement),
        })
        tx.set(productRef.collection('movement').doc(), {
          productID: product.id,
          type: 'deleteTransfer',
          previousInStock: allProductsDict[product.id]?.storeStock?.[info.fromStore.id] || 0,
          increment: -fromIncrement,
          date: fieldValue.serverTimestamp(),
          linkedDocId: transferRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.fromStore.id,
          organizationId: info.organizationId,
        })
        tx.set(productRef.collection('movement').doc(), {
          productID: product.id,
          type: 'deleteTransfer',
          previousInStock: allProductsDict[product.id]?.storeStock?.[info.toStore.id] || 0,
          increment: -toIncrement,
          date: fieldValue.serverTimestamp(),
          linkedDocId: transferRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.toStore.id,
          organizationId: info.organizationId,
        })
      }
    }

    // update daily stats. A transfer opened from the operations list lacks the
    // computed dailyStat doc ids and `date` (those live only on the create-form
    // object). Derive them from the transfer's own date + store ids so the
    // set() calls don't receive an undefined doc id or an undefined `date`
    // field value (Firestore rejects undefined synchronously, before commit).
    const statsDate: Date =
      info.date instanceof Date
        ? info.date
        : (info.date?.toDate?.() ||
          (info.transferDate ? new Date(info.transferDate) : undefined) ||
          new Date())
    const fromDailyStatID = info.fromDailyStatID || getDailyStatID(info.fromStore.id, statsDate)
    const toDailyStatID = info.toDailyStatID || getDailyStatID(info.toStore.id, statsDate)
    const statsAmount = info.amount || 0
    const statsOrgId = info.organizationId || info.organization_id
    // Only write daily stats when a valid doc id resolved (getDailyStatID returns
    // null for a missing store id). The Supabase delete RPC recomputes stats, so
    // skipping the Firestore stats write here is safe and avoids a doc(null) throw.
    if (fromDailyStatID) {
      tx.set(db.collection('dailyStats').doc(fromDailyStatID), {
        transfersAmount: fieldValue.increment(statsAmount),
        date: statsDate,
        store: info.fromStore.id,
        organizationId: statsOrgId,
      }, { merge: true })
    }
    if (toDailyStatID) {
      tx.set(db.collection('dailyStats').doc(toDailyStatID), {
        transfersAmount: fieldValue.increment(-statsAmount),
        date: statsDate,
        store: info.toStore.id,
        organizationId: statsOrgId,
      }, { merge: true })
    }
  })

  await bestEffortDualWriteDelete('transfers.delete', () => transfersApi.deleteTransfer(transferRef.id), { firestoreId: transferRef.id })
}

/* eslint-enable @typescript-eslint/no-explicit-any */
